import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AuthContext from "../../context/AuthContext";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { FontFamily } from "@tiptap/extension-font-family";

import FontSize from "./FontSizeExtension";
import MathExtension from "./MathExtension";
import CustomImageExtension from "./CustomImageExtension";
import MenuBar from "./MenuBar";

const AdminAddBlog = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Underline,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Start writing your blog..." }),
      MathExtension,
      CustomImageExtension,
    ],
    content: "",
  });
<style>
{`
/* PAGE WRAPPER */
.admin-editor-wrapper {
  max-width: 960px;
  margin: 40px auto;
}

/* CARD */
.editor-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0,0,0,0.08);
  overflow: hidden;
}

/* TITLE INPUT */
.editor-title {
  border: none;
  font-size: 1.8rem;
  font-weight: 600;
  padding: 26px 28px;
  outline: none;
  border-bottom: 1px solid #e5e7eb;
}

.editor-title::placeholder {
  color: #9ca3af;
}

/* TOOLBAR */
.toolbar-wrapper {
  margin: 20px;
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

/* TOOLBAR BUTTON SPACING */
.toolbar-wrapper button {
  margin-right: 6px;
  margin-bottom: 6px;
}

/* EDITOR AREA */
.tiptap-editor {
  min-height: 450px;
  padding: 26px;
  font-size: 1.05rem;
  line-height: 1.8;
  outline: none;
}

/* PLACEHOLDER CENTERING */
.tiptap-editor p.is-editor-empty:first-child::before {
  color: #9ca3af;
  font-size: 1rem;
}

/* FOOTER */
.editor-footer {
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* BUTTONS */
.publish-btn {
  padding: 10px 34px;
  font-weight: 600;
  border-radius: 30px;
}
`}
</style>

  /* ================= IMAGE UPLOAD ================= */
const handleImageUpload = async () => {
  if (!editor) return;

  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";

  input.onchange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await api.post("/api/blog/upload-image", formData);

    editor
      .chain()
      .focus()
      .setImage({ src: res.data.url })
      .run();
  };

  input.click();
};


  /* ================= SAVE BLOG ================= */
  const submit = async () => {
    if (!title || !editor || editor.isEmpty) {
      alert("Title and content required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/blog/create", {
        title,
        textContent: JSON.stringify(editor.getJSON()),
        postedByEmail: user.username,
        postedByType: "ADMIN",
      });

      alert("Blog created successfully");
      navigate("/admin/adminroleblogs");
    } catch (err) {
      console.error(err);
      alert("Failed to create blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ================= INTERNAL CSS ================= */}
      <style>
        {`
          .admin-editor-wrapper {
            max-width: 900px;
            margin: 30px auto;
          }
          .editor-card {
            background: #ffffff;
            border-radius: 14px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.08);
            overflow: hidden;
          }
          .editor-title {
            border: none;
            font-size: 1.6rem;
            font-weight: 600;
            padding: 20px;
            outline: none;
          }
          .editor-title::placeholder {
            color: #9ca3af;
          }
          .toolbar-wrapper {
            position: sticky;
            top: 0;
            z-index: 5;
            background: #f9fafb;
            border-top: 1px solid #e5e7eb;
            border-bottom: 1px solid #e5e7eb;
            padding: 8px 12px;
          }
          .tiptap-editor {
            min-height: 420px;
            padding: 20px;
            font-size: 1.05rem;
            line-height: 1.75;
            outline: none;
          }
          .editor-footer {
            background: #ffffff;
            border-top: 1px solid #e5e7eb;
            padding: 15px 20px;
            display: flex;
            justify-content: space-between;
          }
          .publish-btn {
            padding: 8px 28px;
            font-weight: 600;
          }
        `}
      </style>

      {/* ================= UI ================= */}
      <div className="admin-editor-wrapper">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold text-primary mb-0">✍️ Create New Blog</h4>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/admin/adminroleblogs")}
          >
            ← Back
          </button>
        </div>

        <div className="editor-card">
          {/* TITLE */}
          <input
            className="editor-title"
            placeholder="Enter blog title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          {/* TOOLBAR */}
          {editor && (
            <div className="toolbar-wrapper">
              <MenuBar editor={editor} onImageUpload={handleImageUpload} />
            </div>
          )}

          {/* CONTENT */}
          <EditorContent editor={editor} className="tiptap-editor" />

          {/* FOOTER */}
          <div className="editor-footer">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate("/admin/adminroleblogs")}
            >
              Cancel
            </button>

            <button
              className="btn btn-success publish-btn"
              disabled={loading}
              onClick={submit}
            >
              {loading ? "Publishing..." : "🚀 Publish Blog"}
            </button>
            // add noc 
          </div> 
        </div>
      </div>
    </>
  );
};
export default AdminAddBlog;
